import { useEffect, useRef, useState } from 'react'
import './InactivityLockScreen.css'

interface Props {
  onUnlock: () => Promise<boolean>
  onUnlockWithToken?: (token: string) => Promise<boolean>
  message?: string
  isUnauthorized?: boolean
}

export function InactivityLockScreen({ onUnlock, onUnlockWithToken, message, isUnauthorized }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const logoRef = useRef<HTMLImageElement>(null)
  const [showControls, setShowControls] = useState(false)
  const [unlocking, setUnlocking] = useState(false)
  const [showTokenInput, setShowTokenInput] = useState(false)
  const [tokenValue, setTokenValue] = useState('')
  const [tokenError, setTokenError] = useState('')
  const autoTriggered = useRef(false)

  useEffect(() => {
    const t1 = setTimeout(() => {
      logoRef.current?.classList.add('lock-logo-fadein')
    }, 200)
    const t2 = setTimeout(() => {
      setShowControls(true)
      // Auto-trigger biometria tylko gdy mamy WebAuthn (stan locked), nie przy unauthorized
      if (!autoTriggered.current && !isUnauthorized) {
        autoTriggered.current = true
        triggerBiometric()
      }
      // Unauthorized — od razu pokaż formularz tokenu
      if (isUnauthorized) {
        setShowTokenInput(true)
      }
    }, 1200)

    return () => { clearTimeout(t1); clearTimeout(t2) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const triggerBiometric = async () => {
    if (unlocking) return
    setUnlocking(true)
    const success = await onUnlock()
    if (!success) {
      setUnlocking(false)
      // Biometria nie dostępna lub anulowana — pokaż pole tokenu
      setShowTokenInput(true)
    }
  }

  const handleTokenSubmit = async () => {
    const trimmed = tokenValue.trim()
    if (trimmed.length < 20) {
      setTokenError('Token jest za krótki')
      return
    }
    if (!onUnlockWithToken) {
      setTokenError('Odblokowanie tokenem niedostępne')
      return
    }
    setUnlocking(true)
    setTokenError('')
    const success = await onUnlockWithToken(trimmed)
    if (!success) {
      setUnlocking(false)
      setTokenError('Nieprawidłowy token')
    }
  }

  return (
    <div ref={containerRef} className="inactivity-lock">
      <img ref={logoRef} src="/logo.png" className="lock-logo" alt="logo" />
      {showControls && (
        <div className="lock-bottom-area">
          {message && <p className="lock-message">{message}</p>}

          {!showTokenInput && (
            <button
              className="lock-unlock-btn"
              onClick={triggerBiometric}
              disabled={unlocking}
            >
              {unlocking ? 'Weryfikacja…' : 'Odblokuj'}
            </button>
          )}

          {showTokenInput && (
            <div className="lock-token-form">
              <p className="lock-token-label">Wprowadź swój token dostępowy, aby odblokować</p>
              <input
                className="lock-token-input"
                type="password"
                autoComplete="off"
                placeholder="Token dostępowy"
                value={tokenValue}
                onChange={e => { setTokenValue(e.target.value); setTokenError('') }}
                onKeyDown={e => { if (e.key === 'Enter') handleTokenSubmit() }}
              />
              {tokenError && <p className="lock-token-error">{tokenError}</p>}
              <button
                className="lock-unlock-btn"
                onClick={handleTokenSubmit}
                disabled={unlocking || tokenValue.trim().length < 5}
              >
                {unlocking ? 'Weryfikacja…' : 'Zatwierdź'}
              </button>
            </div>
          )}

          {!showTokenInput && !message && (
            <button
              className="lock-token-link"
              onClick={() => setShowTokenInput(true)}
            >
              Użyj tokenu dostępowego
            </button>
          )}
        </div>
      )}
    </div>
  )
}
